import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  App,
  Button,
  Modal,
  Progress,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  LoadingOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import JSZip from "jszip";

import { apiClient } from "../../../services/api/apiClient";
import {
  AuditMapCard,
  type AuditMapCardHandle,
} from "../../audits/components/AuditMapCard";

const { Text } = Typography;

/**
 * Bulk plot-map exporter for a whole planning group.
 *
 * Design (see the design chat for context): iterates through every
 * audit of the planning ONE AT A TIME. For each audit it mounts a
 * hidden `<AuditMapCard>` inside the modal, waits for its `onMapReady`
 * signal (Leaflet + map_data both loaded), calls
 * `composePlotMapImage()` on the imperative handle and stashes the
 * resulting PNG in a `JSZip`. When the queue drains the ZIP is
 * downloaded as `planificacion-<slug>-mapas.zip`.
 *
 * Deliberately client-side: the same helper the individual audit
 * page uses is now called ~N times without a backend proxy. See
 * the fallback timeouts below for the failure paths (missing
 * geometry, dead tile server, etc.).
 */

// Con 600 auditorías + Wake Lock activo, 45s cubre incluso vecinos
// lentos del tile server; el timeout previo de 25s falseaba fallos
// en pestañas semi-backgrounded (Chrome mantiene network pero
// ralentiza rAF → los tiles tardan más en pintarse).
const READY_TIMEOUT_MS = 45_000;
const AFTER_READY_SETTLE_MS = 900;
/** Nº de reintentos por auditoría cuando fallla el `waitForMapReady`
 *  (típicamente el tile server no ha acabado de servir). */
const RETRY_ON_TIMEOUT = 1;
/** Descarga incremental: cada N auditorías completadas hacemos un
 *  flush intermedio del zip al disco. Sin esto, si el navegador
 *  muere a mitad de camino se pierde TODO. Con 100 audits × ~180 KB
 *  el zip parcial cabe holgado en RAM y se descarga rápido. */
const PARTIAL_FLUSH_EVERY = 100;

type AuditStatus =
  | "pending"
  | "running"
  | "done"
  | "failed"
  | "skipped";

interface AuditRow {
  id: number;
  reference: string;
  farm_name: string | null;
  producer_name: string | null;
  audit_type_name: string | null;
  auditor_name: string | null;
  status: AuditStatus;
  error?: string;
  size_kb?: number;
}

interface PlanningAudit {
  id: number;
  reference: string;
  // `AuditListSerializer` denormalises these — the response has
  // `farm_name`, `producer_name`, `audit_type_name` and `auditor_name`
  // at the root, NOT nested under `farm`/`audit_type`. Reading the
  // nested paths silently returned undefined and the ZIP mapas came
  // out sin cabecera de finca/productor.
  farm_name?: string | null;
  producer_name?: string | null;
  audit_type_name?: string | null;
  auditor_name?: string | null;
  status_of_audit?: string;
}

interface Props {
  open: boolean;
  planningId: number;
  planningName: string;
  onClose: () => void;
}

const slugify = (s: string): string =>
  (s || "planning")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "planning";

const csvEscape = (v: unknown): string => {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const fmtDuration = (ms: number): string => {
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export const PlanningPlotMapsExportModal = ({
  open,
  planningId,
  planningName,
  onClose,
}: Props) => {
  // Ant 5: modal via useApp() para que participe del <App> context
  const { modal } = App.useApp();
  const [audits, setAudits] = useState<AuditRow[]>([]);
  const [phase, setPhase] = useState<
    "loading" | "idle" | "running" | "paused" | "done" | "canceled"
  >("loading");
  const [cursor, setCursor] = useState<number>(0);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState<number>(Date.now());
  // Contabilidad del tiempo en pausa: `pausedAtMs` marca el instante en
  // que el usuario pausó (null cuando no está en pausa) y
  // `pausedTotalMs` acumula las pausas completadas. Se resta al elapsed
  // para que el contador NO siga corriendo mientras la exportación
  // está pausada.
  const [pausedAtMs, setPausedAtMs] = useState<number | null>(null);
  const [pausedTotalMs, setPausedTotalMs] = useState<number>(0);
  const [tabHidden, setTabHidden] = useState<boolean>(
    typeof document !== "undefined" && document.visibilityState === "hidden",
  );
  /** Segmento actual del zip para descarga incremental. Empieza en 0
   *  y sube cada vez que hacemos flush a disco. */
  const [zipPart, setZipPart] = useState<number>(1);

  // Refs — the imperative moving parts don't need to trigger renders.
  const zipRef = useRef<JSZip | null>(null);
  const mapRef = useRef<AuditMapCardHandle>(null);
  const readyResolveRef = useRef<(() => void) | null>(null);
  const abortRef = useRef<boolean>(false);
  /** Contador de fotos añadidas al segmento actual del zip — cuando
   *  llega a `PARTIAL_FLUSH_EVERY` disparamos flush parcial y reset. */
  const partCountRef = useRef<number>(0);
  /** Wake Lock para evitar que el sistema entre en suspensión durante
   *  la exportación larga. Se libera al terminar / pausar. */
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  // Snapshot of `phase` for the async iterator — reading state
  // inside the loop closure would be stale, so we mirror it here.
  const phaseRef = useRef(phase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // Timer solo mientras corre — al pausar congelamos elapsedMs vía
  // `pausedAtMs`, así que no hace falta seguir actualizando nowMs.
  useEffect(() => {
    if (phase !== "running") return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [phase]);

  // Wake Lock — cuando el navegador soporta la API, pedimos "screen"
  // para que el sistema no entre en suspensión durante la exportación
  // (600 audits × ~5s = 50min, suficiente para que un portátil se
  // duerma). El navegador libera solo el lock al perder foco varios
  // minutos; lo volvemos a pedir al volver a foco.
  useEffect(() => {
    if (phase !== "running") return;
    const nav = navigator as any;
    if (!nav?.wakeLock?.request) return;
    let released = false;
    const acquire = async () => {
      try {
        const sentinel = await nav.wakeLock.request("screen");
        if (released) {
          sentinel.release?.();
          return;
        }
        wakeLockRef.current = sentinel;
      } catch {
        // Ignorar — no crítico; solo hace la exportación más frágil
        // en pestañas backgrounded.
      }
    };
    void acquire();
    const onVisibility = () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current) {
        void acquire();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisibility);
      wakeLockRef.current?.release?.().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [phase]);

  // Aviso claro cuando la pestaña se va a background — Chrome
  // ralentiza rAF, los tiles Leaflet no acaban de pintar y el
  // waitForMapReady se cae por timeout en cada auditoría.
  useEffect(() => {
    const onVisibility = () => {
      setTabHidden(document.visibilityState === "hidden");
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Fetch the audit list once when the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPhase("loading");
    (async () => {
      try {
        const { data } = await apiClient.get<{
          data?: PlanningAudit[];
          results?: PlanningAudit[];
        }>("/v1/audits/", {
          params: { planning_group: planningId, page_size: 1000 },
        });
        const list = data?.data ?? data?.results ?? [];
        const rows: AuditRow[] = list
          .filter((a) => a.status_of_audit !== "canceled")
          .map((a) => ({
            id: a.id,
            reference: a.reference,
            farm_name: a.farm_name ?? null,
            producer_name: a.producer_name ?? null,
            audit_type_name: a.audit_type_name ?? null,
            auditor_name: a.auditor_name ?? null,
            status: "pending" as AuditStatus,
          }));
        if (cancelled) return;
        setAudits(rows);
        setPhase("idle");
        setCursor(0);
        zipRef.current = new JSZip();
      } catch (err: any) {
        if (cancelled) return;
        message.error(
          err?.response?.data?.detail ??
            err?.message ??
            "No se pudo cargar la lista de auditorías.",
        );
        setPhase("idle");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, planningId]);

  // Reset state when the modal actually closes so re-opening is
  // a fresh run (unless a run is in progress — we keep it going).
  useEffect(() => {
    if (open) return;
    if (phaseRef.current === "running") return;
    setAudits([]);
    setPhase("loading");
    setCursor(0);
    setStartedAt(null);
    zipRef.current = null;
    abortRef.current = false;
  }, [open]);

  const doneCount = useMemo(
    () => audits.filter((r) => r.status === "done").length,
    [audits],
  );
  const failedCount = useMemo(
    () => audits.filter((r) => r.status === "failed").length,
    [audits],
  );

  // Descontamos las pausas — si el usuario está pausando ahora,
  // congelamos el reloj en el instante de la pausa. Si ya reanudó,
  // solo restamos las pausas ya acumuladas.
  const elapsedMs =
    startedAt !== null
      ? Math.max(
          0,
          (pausedAtMs ?? nowMs) - startedAt - pausedTotalMs,
        )
      : 0;
  const processed = doneCount + failedCount;
  const remaining = audits.length - processed;
  const etaMs =
    processed > 0 ? (elapsedMs / processed) * remaining : 0;

  const patchRow = useCallback(
    (id: number, patch: Partial<AuditRow>) => {
      setAudits((prev) =>
        prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
      );
    },
    [],
  );

  const waitForMapReady = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        readyResolveRef.current = null;
        reject(
          new Error(
            `Timeout esperando el mapa (${Math.round(READY_TIMEOUT_MS / 1000)}s).`,
          ),
        );
      }, READY_TIMEOUT_MS);
      readyResolveRef.current = () => {
        clearTimeout(timeoutId);
        readyResolveRef.current = null;
        resolve();
      };
    });
  }, []);

  const onMapReady = useCallback(() => {
    readyResolveRef.current?.();
  }, []);

  const buildZipAndDownload = useCallback(
    async (isPartial: boolean = false) => {
      const zip = zipRef.current;
      if (!zip) return;

      // _index.csv listing every audit that was in the queue and its
      // final status. Kept even for cancel/partial exports so the user
      // can see what got in and what didn't.
      const csv = [
        "referencia,productor,finca,tipo,estado,tamaño_kb,error",
        ...audits.map((r) =>
          [
            r.reference,
            r.producer_name ?? "",
            r.farm_name ?? "",
            r.audit_type_name ?? "",
            r.status,
            r.size_kb ?? "",
            r.error ?? "",
          ]
            .map(csvEscape)
            .join(","),
        ),
      ].join("\n");
      zip.file("_index.csv", csv);

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Cuando hacemos flush parcial etiquetamos con `-parteN` para
      // que el auditor sepa el orden. El último (final o cancel) no
      // lleva sufijo si `zipPart === 1` (o sea, todo cupo en uno).
      const suffix = isPartial || zipPart > 1 ? `-parte${zipPart}` : "";
      link.download = `planificacion-${slugify(planningName)}-mapas${suffix}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    [audits, planningName, zipPart],
  );

  /** Descarga el zip actual y arranca uno nuevo — se llama cada
   *  PARTIAL_FLUSH_EVERY audits. Evita que la RAM crezca sin control
   *  y protege la sesión si el navegador muere. */
  const flushPartialZip = useCallback(async () => {
    await buildZipAndDownload(true);
    zipRef.current = new JSZip();
    partCountRef.current = 0;
    setZipPart((n) => n + 1);
  }, [buildZipAndDownload]);

  // Iterator — driven by the cursor state. When cursor advances, an
  // effect below picks up the change and mounts the next audit's
  // map. The current audit's compose is triggered by `onMapReady`
  // firing → we call composePlotMapImage → stash blob → advance.
  //
  // Retry: si `waitForMapReady` timeoutea, reintentamos hasta
  // `RETRY_ON_TIMEOUT` veces. Basta con forzar un remount del
  // AuditMapCard: como el `key` está atado al audit.id, cambiamos
  // el offset via un flag efímero. En la práctica pedimos al
  // caller (el effect) que nos remontee — pero sin cambiar
  // cursor. Para minimizar cirugía, dentro del propio try
  // volvemos a esperar; con el settle previo Leaflet normalmente
  // recupera los tiles a la segunda.
  const processCurrent = useCallback(async () => {
    const row = audits[cursor];
    if (!row) return;

    patchRow(row.id, { status: "running" });

    let attempt = 0;
    let lastErr: Error | null = null;
    while (attempt <= RETRY_ON_TIMEOUT) {
      try {
        await waitForMapReady();
        // Give tiles a beat to finish drawing after the "ready" gate.
        await new Promise((r) => setTimeout(r, AFTER_READY_SETTLE_MS));
        if (abortRef.current) throw new Error("Cancelado");
        const composed = await mapRef.current?.composePlotMapImage();
        if (!composed) {
          throw new Error("El mapa no tenía parcelas visibles.");
        }
        const sizeKb = Math.round(composed.blob.size / 1024);
        const filename = `${row.reference}-${slugify(row.producer_name ?? "")}-${slugify(row.farm_name ?? "")}.png`;
        zipRef.current?.file(filename, composed.blob);
        partCountRef.current += 1;
        patchRow(row.id, { status: "done", size_kb: sizeKb });
        lastErr = null;
        break;
      } catch (err: any) {
        lastErr = err instanceof Error ? err : new Error(String(err));
        readyResolveRef.current = null;
        // Solo reintentamos si es un timeout de tiles — errores
        // "sin parcelas" o Cancelado no ganan nada con un retry.
        const isTimeout = /Timeout esperando el mapa/i.test(lastErr.message);
        if (!isTimeout || attempt >= RETRY_ON_TIMEOUT) break;
        attempt += 1;
        // Pequeño respiro para que el tile server se calme.
        await new Promise((r) => setTimeout(r, 1500));
      }
    }
    if (lastErr) {
      const msg = lastErr.message;
      patchRow(row.id, { status: "failed", error: msg });
      zipRef.current?.file(
        `_errores/${row.reference}.txt`,
        `Error generando el mapa para la auditoría ${row.reference} `
          + `tras ${attempt + 1} intento(s):\n${msg}`,
      );
    }
    readyResolveRef.current = null;
  }, [audits, cursor, patchRow, waitForMapReady]);

  // Drive the iterator: whenever cursor changes AND we're running,
  // process the current audit and advance. Splitting this into an
  // effect (instead of a plain loop) is what lets React unmount the
  // previous AuditMapCard and mount a fresh one for the next audit,
  // which is what makes `onMapReady` fire cleanly.
  useEffect(() => {
    if (phase !== "running") return;
    if (cursor >= audits.length) {
      setPhase("done");
      buildZipAndDownload();
      return;
    }
    let mounted = true;
    (async () => {
      await processCurrent();
      // Flush parcial: si acumulamos PARTIAL_FLUSH_EVERY audits en el
      // segmento actual, disparamos descarga y reset del zip. Protege
      // ante crashes del navegador con planificaciones grandes.
      if (partCountRef.current >= PARTIAL_FLUSH_EVERY) {
        await flushPartialZip();
      }
      // Small delay between audits to let React commit the unmount
      // of the previous map before the next mounts — otherwise the
      // Leaflet cleanup races the new instance.
      await new Promise((r) => setTimeout(r, 300));
      if (!mounted) return;
      if (phaseRef.current === "paused") return;
      if (abortRef.current) return;
      setCursor((c) => c + 1);
    })();
    return () => {
      mounted = false;
    };
    // We only want this to fire when the cursor or phase transitions
    // to running — `processCurrent` closes over both.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, phase]);

  const handleStart = () => {
    if (audits.length === 0) return;
    zipRef.current = new JSZip();
    partCountRef.current = 0;
    setZipPart(1);
    abortRef.current = false;
    setStartedAt(Date.now());
    setNowMs(Date.now());
    setPausedAtMs(null);
    setPausedTotalMs(0);
    setCursor(0);
    setPhase("running");
  };

  const handlePause = () => {
    setPausedAtMs(Date.now());
    setPhase("paused");
  };

  const handleResume = () => {
    // Acumulamos la duración de la pausa que acaba y descartamos el
    // marcador — así elapsedMs deja de restar y sigue corriendo desde
    // el punto donde se paró.
    if (pausedAtMs !== null) {
      setPausedTotalMs((total) => total + (Date.now() - pausedAtMs));
      setPausedAtMs(null);
    }
    setPhase("running");
    // Re-kick the effect by nudging the cursor to the same value.
    setCursor((c) => c);
  };

  const handleCancel = () => {
    modal.confirm({
      title: "Cancelar exportación",
      content:
        doneCount > 0
          ? `Ya se han generado ${doneCount} mapas. Puedes descargar los que hay antes de cancelar.`
          : "Se abandonará la exportación sin descargar nada.",
      okText: doneCount > 0 ? "Descargar parcial y cancelar" : "Cancelar",
      okButtonProps: { danger: true },
      cancelText: "Seguir",
      onOk: async () => {
        abortRef.current = true;
        setPhase("canceled");
        if (doneCount > 0) await buildZipAndDownload();
      },
    });
  };

  const columns = [
    {
      title: "Ref.",
      dataIndex: "reference",
      width: 100,
      render: (v: string) => <strong>{v}</strong>,
    },
    {
      title: "Productor",
      dataIndex: "producer_name",
      ellipsis: true,
      render: (v: string | null) => v || "—",
    },
    {
      title: "Finca",
      dataIndex: "farm_name",
      ellipsis: true,
      render: (v: string | null) => v || "—",
    },
    {
      title: "Estado",
      dataIndex: "status",
      // Wider so el mensaje de error inline quepa sin cortar (antes 130
      // dejaba solo hueco a la Tag "Error" y el motivo se ocultaba en un
      // tooltip que pasaba desapercibido).
      width: 260,
      render: (v: AuditStatus, row: AuditRow) => {
        if (v === "pending")
          return <Tag color="default">Pendiente</Tag>;
        if (v === "running")
          return (
            <Tag icon={<LoadingOutlined />} color="processing">
              Procesando
            </Tag>
          );
        if (v === "done")
          return (
            <Tag icon={<CheckCircleOutlined />} color="green">
              {row.size_kb ? `${row.size_kb} KB` : "Listo"}
            </Tag>
          );
        if (v === "skipped") return <Tag color="default">Saltada</Tag>;
        // Fallo: Tag rojo + motivo inline. El texto completo queda en
        // el tooltip por si el mensaje excede el ancho de la celda.
        return (
          <Space size={6} style={{ maxWidth: "100%" }}>
            <Tag icon={<CloseCircleOutlined />} color="red" style={{ margin: 0 }}>
              Error
            </Tag>
            {row.error ? (
              <Tooltip title={row.error} placement="topLeft">
                <span
                  style={{
                    fontSize: 12,
                    color: "#a8071a",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 180,
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                >
                  {row.error}
                </span>
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
  ];

  const currentRow = audits[cursor];
  const showOffscreen = phase === "running" && currentRow !== undefined;

  return (
    <Modal
      open={open}
      onCancel={() => {
        if (phase === "running") {
          message.warning(
            "Pausa o cancela la exportación antes de cerrar.",
          );
          return;
        }
        onClose();
      }}
      title={
        <Space>
          <DownloadOutlined />
          <span>Descargar mapas de parcelas</span>
          <Tag color="blue">{planningName}</Tag>
        </Space>
      }
      width={860}
      footer={
        <Space>
          {phase === "idle" && (
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleStart}
              disabled={audits.length === 0}
            >
              Iniciar ({audits.length})
            </Button>
          )}
          {phase === "running" && (
            <>
              <Button
                icon={<PauseCircleOutlined />}
                onClick={handlePause}
              >
                Pausar
              </Button>
              <Button
                danger
                icon={<StopOutlined />}
                onClick={handleCancel}
              >
                Cancelar
              </Button>
            </>
          )}
          {phase === "paused" && (
            <>
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleResume}
              >
                Reanudar
              </Button>
              <Button
                danger
                icon={<StopOutlined />}
                onClick={handleCancel}
              >
                Cancelar
              </Button>
            </>
          )}
          {(phase === "done" || phase === "canceled") && (
            <Button type="primary" onClick={onClose}>
              Cerrar
            </Button>
          )}
        </Space>
      }
    >
      {phase === "loading" && (
        <div style={{ textAlign: "center", padding: 24 }}>
          Cargando auditorías…
        </div>
      )}

      {phase !== "loading" && audits.length === 0 && (
        <Alert
          type="info"
          message="Esta planificación no tiene auditorías activas."
        />
      )}

      {/* Aviso pre-inicio: el proceso es cliente-side y Chrome
          ralentiza rAF si la pestaña queda en background → los mapas
          no acaban de pintar. Con >100 audits recomendamos dejar
          la pestaña activa. */}
      {phase === "idle" && audits.length > 50 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Planificación grande: mantén esta pestaña abierta y en primer plano"
          description={
            <>
              Se generarán {audits.length} mapas uno a uno. Si cambias de
              pestaña, el navegador ralentiza el renderizado de los tiles
              y algunas auditorías pueden fallar. Cada {PARTIAL_FLUSH_EVERY}{" "}
              mapas se descarga un ZIP parcial para que no pierdas
              progreso.
            </>
          }
        />
      )}

      {/* Warning dinámico: si el usuario cambia de pestaña durante
          la ejecución, avisamos claramente que el proceso está
          ralentizado. */}
      {(phase === "running" || phase === "paused") && tabHidden && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Pestaña en segundo plano — el navegador está ralentizando la exportación"
          description="Vuelve a esta pestaña para que los mapas se generen a velocidad normal."
        />
      )}

      {audits.length > 0 && (
        <>
          {(phase === "running" ||
            phase === "paused" ||
            phase === "done" ||
            phase === "canceled") && (
            <div style={{ marginBottom: 12 }}>
              <Progress
                percent={
                  audits.length > 0
                    ? Math.round(
                        ((doneCount + failedCount) / audits.length) * 100,
                      )
                    : 0
                }
                status={
                  phase === "canceled"
                    ? "exception"
                    : phase === "done"
                    ? "success"
                    : "active"
                }
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 12,
                  marginTop: 12,
                }}
              >
                <Statistic
                  title="Procesadas"
                  value={`${doneCount + failedCount} / ${audits.length}`}
                />
                <Statistic
                  title="Errores"
                  value={failedCount}
                  valueStyle={{
                    color: failedCount > 0 ? "#cf1322" : undefined,
                  }}
                />
                <Statistic
                  title="Tiempo"
                  value={fmtDuration(elapsedMs)}
                />
                <Statistic
                  title="ETA"
                  value={
                    phase === "running" && etaMs > 0
                      ? fmtDuration(etaMs)
                      : "—"
                  }
                />
              </div>
              {phase === "running" && currentRow && (
                <div
                  style={{
                    marginTop: 8,
                    padding: 8,
                    background: "#f5f5f5",
                    borderRadius: 6,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Ahora:{" "}
                    <strong>{currentRow.reference}</strong>
                    {currentRow.farm_name
                      ? ` · ${currentRow.farm_name}`
                      : ""}
                    {currentRow.producer_name
                      ? ` · ${currentRow.producer_name}`
                      : ""}
                  </Text>
                </div>
              )}
            </div>
          )}

          <Table
            size="small"
            rowKey="id"
            dataSource={audits}
            columns={columns as any}
            pagination={{ pageSize: 10, size: "small" }}
            scroll={{ y: 320 }}
          />
        </>
      )}

      {/* Off-screen renderer for the current audit's map. Kept in
           the DOM only while running so the AuditMapCard mounts,
           fires onMapReady, gets compose()d, and unmounts for the
           next audit.

           IMPORTANTE: NO usar `visibility:hidden` ni `display:none`.
           html-to-image clona el DOM y hereda esos estilos → la
           imagen resultante sale en blanco (sin tiles ni polígonos).
           `left:-20000` es suficiente para que el usuario no lo vea;
           `pointerEvents:none` bloquea la interacción por si acaso. */}
      {showOffscreen && currentRow && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            left: -20000,
            top: 0,
            width: 900,
            height: 720,
            pointerEvents: "none",
          }}
        >
          <AuditMapCard
            key={currentRow.id}
            ref={mapRef}
            auditId={currentRow.id}
            auditReference={currentRow.reference}
            producerName={currentRow.producer_name ?? undefined}
            farmName={currentRow.farm_name ?? undefined}
            mapHeight="640px"
            readOnly
            onMapReady={onMapReady}
          />
        </div>
      )}
    </Modal>
  );
};

export default PlanningPlotMapsExportModal;
